import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";

export interface HoursCompletedResult {
  hoursProgress?: string[];  // corresponds to "Billable Table"
  weeklyHours?: string[];
  usedHours?: string[];
  isLeadership?: boolean;
  pacePercentage?: number;  // raw decimal, e.g. 0.98 = 98%
  loading: boolean;
  error?: string;
}

export const useHoursCompleted = (
  username?: string,
  year?: string,
  quarter?: number,
  status?: string,
  enabled: boolean = true
): HoursCompletedResult => {
  const client = useMyProviderDataClient();

  const [hoursProgress, setHoursProgress] = useState<string[]>();
  const [weeklyHours, setWeeklyHours] = useState<string[]>();
  const [usedHours, setUsedHours] = useState<string[]>();
  const [isLeadership, setIsLeadership] = useState<boolean>();
  const [pacePercentage, setPacePercentage] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!enabled || !username) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 1️⃣ Fetch hours completed
        const res = await client.get("/EmployeeInformation/HoursCompleted", {
          params: { username, year, quarter },
        });

        const data = res.data;

        if (
          data &&
          (data.averageHoursPerWeek != null ||
          data.ptoSickFH != null ||
          data.vacationCredit != null ||
          data.holidayHours != null ||
          data.hoursBilled != null ||
          data.proratedHoursRequired != null ||
          data.meetingHours != null ||
          data.hoursRequired != null ||
          data.percentageComplete != null
          )
        ) {
          let averageHoursPerWeek = data.averageHoursPerWeek == null ? 0 : data.averageHoursPerWeek;
          let ptoSickFH = data.ptoSickFH == null ? 0 : data.ptoSickFH;
          let vacationCredit = data.vacationCredit == null ? 0 : data.vacationCredit;
          let holidayHours = data.holidayHours == null ? 0 : data.holidayHours;
          let hoursBilled = data.hoursBilled == null ? 0 : data.hoursBilled;
          let proratedHoursRequired = data.proratedHoursRequired == null ? 0 : data.proratedHoursRequired;
          let meetingHours = data.meetingHours == null ? 0 : data.meetingHours;
          let hoursRequired = data.hoursRequired == null ? 0 : data.hoursRequired;
          let percentageComplete = data.percentageComplete == null ? 0 : data.percentageComplete;
          

          // Billable Table / Hours Progress
          const progress =
            status === "Exempt"
              ? [
                  String(proratedHoursRequired.toFixed(2)),
                  String(hoursBilled.toFixed(2)),
                  `${(percentageComplete * 100).toFixed(2)}%`,
                ]
              : ["N/A", String(hoursBilled.toFixed(2)), "N/A"];

          // Weekly Hours
          const weekly = [
            String(hoursRequired ?? 0),
            String(averageHoursPerWeek.toFixed(1)),
          ];

          // Used Hours
          const used = [
            String(meetingHours),
            String(holidayHours),
            String(ptoSickFH),
            String(vacationCredit),
          ];

          setHoursProgress(progress);
          setWeeklyHours(weekly);
          setUsedHours(used);
          setIsLeadership(data.isLeadership === true);
          setPacePercentage(percentageComplete);
        } else {
          setHoursProgress(undefined);
          setWeeklyHours(undefined);
          setUsedHours(undefined);
          setIsLeadership(undefined);
          setPacePercentage(undefined);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load hours completed.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [username, year, quarter, enabled, client, status]);

  return { hoursProgress, weeklyHours, usedHours, isLeadership, pacePercentage, loading, error };
};